/**
 * @poc-to-production
 *
 * Browser-local audit store for architect-supplied facts. Fastest path for
 * the static demo — append-only revisions in localStorage keyed by project.
 * Production replaces this with Postgres + a server-side audit API.
 */
import type { UserFactValue } from "../stores/userFactStore";

const STORAGE_PREFIX = "vera-user-fact-audit-v1";

export interface UserFactRevision {
  projectId: string;
  dbId: number;
  factKey: string;
  label?: string;
  ruleId?: string;
  value: UserFactValue;
  version: number;
  suppliedAt: string;
  updatedAt: string;
  /** When copied from another element via bulk apply */
  copiedFromDbId?: number;
  copiedFromVersion?: number;
}

interface ProjectAuditFile {
  projectId: string;
  revisions: UserFactRevision[];
}

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}:${projectId}`;
}

function readProjectFile(projectId: string): ProjectAuditFile {
  if (typeof localStorage === "undefined") {
    return { projectId, revisions: [] };
  }
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return { projectId, revisions: [] };
    const parsed = JSON.parse(raw) as ProjectAuditFile;
    if (parsed.projectId !== projectId || !Array.isArray(parsed.revisions)) {
      return { projectId, revisions: [] };
    }
    return parsed;
  } catch {
    return { projectId, revisions: [] };
  }
}

function writeProjectFile(file: ProjectAuditFile): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey(file.projectId), JSON.stringify(file));
  } catch (err) {
    console.warn("[Vera] user fact audit persist failed:", err);
  }
}

/** Latest value per dbId+factKey for engine merge. */
export function latestOverridesFromAudit(
  projectId: string
): Record<number, Record<string, UserFactValue>> {
  const file = readProjectFile(projectId);
  const out: Record<number, Record<string, UserFactValue>> = {};
  for (const rev of file.revisions) {
    if (!out[rev.dbId]) out[rev.dbId] = {};
    out[rev.dbId][rev.factKey] = rev.value;
  }
  return out;
}

/** Full revision history for a fact (newest last). */
export function revisionHistory(
  projectId: string,
  dbId: number,
  factKey: string
): UserFactRevision[] {
  return readProjectFile(projectId).revisions.filter(
    r => r.dbId === dbId && r.factKey === factKey
  );
}

/** Append a new revision (bumps version). Returns the saved revision. */
export function appendUserFactRevision(input: {
  projectId: string;
  dbId: number;
  factKey: string;
  label?: string;
  ruleId?: string;
  value: UserFactValue;
  copiedFromDbId?: number;
  copiedFromVersion?: number;
}): UserFactRevision {
  const file = readProjectFile(input.projectId);
  const prior = file.revisions.filter(
    r => r.dbId === input.dbId && r.factKey === input.factKey
  );
  const last = prior[prior.length - 1];
  const now = new Date().toISOString();
  const version = (last?.version ?? 0) + 1;
  const revision: UserFactRevision = {
    projectId: input.projectId,
    dbId: input.dbId,
    factKey: input.factKey,
    label: input.label,
    ruleId: input.ruleId,
    value: input.value,
    version,
    suppliedAt: last?.suppliedAt ?? now,
    updatedAt: now,
    ...(input.copiedFromDbId != null
      ? {
          copiedFromDbId: input.copiedFromDbId,
          copiedFromVersion: input.copiedFromVersion,
        }
      : {}),
  };
  file.revisions.push(revision);
  writeProjectFile(file);
  return revision;
}

/** Audit metadata for all user facts on an element (latest revision each). */
export function latestUserFactsForElement(
  projectId: string,
  dbId: number
): UserFactRevision[] {
  const file = readProjectFile(projectId);
  const byKey = new Map<string, UserFactRevision>();
  for (const rev of file.revisions) {
    if (rev.dbId !== dbId) continue;
    byKey.set(rev.factKey, rev);
  }
  return [...byKey.values()];
}

/** Latest revisions saved while answering a specific rule on this element. */
export function latestUserFactsForElementRule(
  projectId: string,
  dbId: number,
  ruleId: string
): UserFactRevision[] {
  return latestUserFactsForElement(projectId, dbId).filter(
    r => r.ruleId === ruleId
  );
}
